import { useEffect, useState } from 'react';
import type { StickerPack, StickerRef } from '@kinop/shared';
import { getStickerPack } from '../lib/api.js';
import {
  getStickerLibrary,
  installPack,
  rememberRecentSticker,
  uninstallPack,
} from '../lib/storage.js';
import { StickerImage } from './StickerPicker.js';
import { Button } from '@/components/ui/button';

interface StickerPackSheetProps {
  /** Набор, к которому относится нажатый стикер. */
  setName: string;
  onClose: () => void;
  onPick: (sticker: StickerRef) => void;
}

/**
 * Набор, открытый по нажатию на стикер в переписке.
 *
 * Так это устроено и в телеграме: увидел чужой стикер — нажал — посмотрел
 * весь набор — забрал себе. Без этого набор можно было добавить только
 * ссылкой, которую ещё надо где-то взять.
 */
export function StickerPackSheet({
  setName,
  onClose,
  onPick,
}: StickerPackSheetProps): JSX.Element {
  const [pack, setPack] = useState<StickerPack | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [installed, setInstalled] = useState(() =>
    getStickerLibrary().installed.includes(setName),
  );

  useEffect(() => {
    let cancelled = false;
    void getStickerPack(setName)
      .then((result) => {
        if (!cancelled) setPack(result.pack);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setName]);

  // Закрытие по Esc: окно перекрывает переписку, и выход должен быть под рукой.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="pack-sheet" role="dialog" aria-modal="true" aria-label="Набор стикеров">
      <div className="pack-sheet__head">
        <span className="pack-sheet__title">
          {pack?.title ?? (isLoading ? 'Открываем…' : 'Набор не найден')}
        </span>
        <button
          type="button"
          className="pack-sheet__close"
          aria-label="Закрыть"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {pack && (
        <>
          <div
            className={`stickers__grid ${
              pack.kind === 'custom_emoji' ? 'stickers__grid--emoji' : ''
            }`}
          >
            {pack.stickers.map((sticker) => (
              <button
                key={sticker.id}
                type="button"
                className="stickers__item"
                aria-label={`Отправить стикер ${sticker.emoji ?? ''}`.trim()}
                onClick={() => {
                  rememberRecentSticker(sticker.id);
                  onPick(sticker);
                  onClose();
                }}
              >
                <StickerImage sticker={sticker} />
              </button>
            ))}
          </div>

          <div className="pack-sheet__foot">
            <span className="mono-label">{pack.stickers.length} шт.</span>
            <Button
              size="sm"
              variant={installed ? 'ghost' : 'default'}
              onClick={() => {
                if (installed) uninstallPack(pack.name);
                else installPack(pack.name);
                setInstalled(!installed);
              }}
            >
              {installed ? 'Убрать набор' : 'Добавить себе'}
            </Button>
          </div>
        </>
      )}

      {!pack && !isLoading && (
        <p className="stickers__empty">
          Набор больше не доступен — возможно, его удалили в телеграме.
        </p>
      )}
    </div>
  );
}
